"use client";

import Image, { StaticImageData } from "next/image";
import React, { useState } from "react";
import WhyICM1 from "@/app/assets/images/why-icm-1.png";

import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/stories/accordian/accordian";

interface DataType {
    id: number;
    title: string;
    img: StaticImageData | string;
    content: string;
}

interface WhyICMProps {
    title: string;
    data: DataType[];
    page?: string;
    heading?: string;
    classNames?: string;
}

const WhyICM: React.FC<WhyICMProps> = ({
    title,
    data,
    page,
    heading = "Why Choose InCred Money?",
    classNames,
}) => {
    const [currID, setCurrID] = useState<number>(1);
    const [isLoaded, setIsLoaded] = useState<boolean>(false);

    const currentImage = data.find((item) => item.id === currID)?.img || WhyICM1;

    return (
        <div
            id="why-icm-container"
            className={`h-auto pt-s15 md:pt-0 ${classNames} ${page === "fd" ? "mx-auto md:w-[1152px]" : "w-full"}`}
        >
            <div id="why-icm-header" className="mb-s11 flex flex-col items-center md:mb-s17">
                {page !== "fd" && (
                    <h2
                        id="why-icm-subtitle"
                        className="mb-1 text-center text-4xl font-semibold leading-[130%] tracking-[-2%] text-neutral-900 md:text-7xl md:leading-[120%] md:tracking-[-1%]"
                    >
                        {heading}
                    </h2>
                )}
                <div
                    id="why-icm-title"
                    className="test-sm text-center text-neutral-300 md:text-neutral-900 leading-[22.4px] font-semibold md:font-regular tracking-[-0.14px] sm:w-3/5 md:mb-0 text-sm md:text-lg md:leading-[164%] md:tracking-[-0.18px]"
                    dangerouslySetInnerHTML={{ __html: title }}
                ></div>
            </div>
            <div
                id="why-icm-desktop-view"
                className="hidden h-full min-h-[355px] items-center justify-between gap-[89px] md:flex"
            >
                <div id="why-icm-accordion-container" className="flex flex-1 self-start">
                    <Accordion
                        id="why-icm-accordion"
                        type="single"
                        collapsible={true}
                        className="w-full max-w-full"
                        defaultValue="item-1"
                    >
                        {data.map(({ id, title, content }) => (
                            <AccordionItem
                                id={`accordion-item-${id}`}
                                key={id}
                                value={`item-${id}`}
                                className="data-[state=open]:border-b-2"
                                onClick={() => setCurrID(id)}
                            >
                                <AccordionTrigger
                                    id={`accordion-trigger-${id}`}
                                    showIcon={true}
                                    className={`gap-4 text-left text-3xl font-semibold leading-[32px] [&>svg]:size-5 ${id !== currID ? "py-8" : "pb-3"}`}
                                >
                                    {title}
                                </AccordionTrigger>
                                <AccordionContent
                                    id={`accordion-content-${id}`}
                                    className="pb-8 text-lg font-medium"
                                >
                                    {content}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
                <div className="max-h-[380px] max-w-[463px] ">
                    <Image
                        id="why-icm-image"
                        src={currentImage}
                        alt="Why ICM"
                        width={100}
                        height={100}
                        className={`h-full max-h-[380px] w-full object-cover transition-opacity duration-500 ${
                            isLoaded ? "opacity-100" : "opacity-0"
                        }`}
                        onLoad={() => setIsLoaded(true)}
                    />
                </div>
            </div>

            <div id="why-icm-mobile-view" className="flex flex-col items-center md:hidden">
                {data.map(({ id, img, title, content }) => (
                    <div id={`mobile-card-${id}`} key={id}>
                        <div
                            id={`mobile-image-container-${id}`}
                            className="m-auto h-full w-[250px] py-5"
                        >
                            <Image
                                id={`mobile-image-${id}`}
                                style={{ objectFit: "cover" }}
                                src={img}
                                alt="Why ICM"
                                width={0}
                                height={0}
                                className="w-full"
                            />
                        </div>
                        <div id={`mobile-text-container-${id}`} className="">
                            <h3
                                id={`mobile-title-${id}`}
                                className="mb-3 text-center text-xl font-semibold"
                            >
                                {title}
                            </h3>
                            <p
                                id={`mobile-content-${id}`}
                                className="mb-14 text-center text-sm font-normal"
                            >
                                {content}
                            </p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default WhyICM;
